import React, { useState, useEffect } from "react";
import { withTranslation, Trans } from "react-i18next";
import { bindActionCreators } from "redux";
import { useSelector, useDispatch } from "react-redux";
import moment from "moment";
import WithSelect from "../../../components/select";
import StatusBadge from "../../../components/statusbadge/StatusBadge";
import * as myJobsActions from "../../../modules/myJobs";
import * as notificationActions from "../../../modules/notification";
import { UPLOAD_FILE_LOCATION_TYPE, CONSTANTS } from "../../../constants";
import convertToByte from "../../../cores/convertToByte";
import ObjectIcon from "../../../routes/sources/components/ObjectIcon";

const actionTypes = [
    { label: "All Type", value: "ALL" },
    { label: "Move", value: "MOVE" },
    { label: "Copy to", value: "COPY" },
    { label: "Delete", value: "DELETE" },
    { label: "Rename", value: "RENAME" },
];

let throttleHandler = false;

const FileJobPanel = (props) => {
    const { t } = props;

    const [limit, setLimit] = useState(20);
    const [viewmoreEnable, setViewmoreEnable] = useState(false);

    const {
        myJobs,
    } = useSelector(state => state);
    const dispatch = useDispatch();


    const MyJobsActions = bindActionCreators(myJobsActions, dispatch);
    const NotificationActions = bindActionCreators(notificationActions, dispatch);

    useEffect(() => {
        if (myJobs.totalCounts > myJobs.data.length) {
            setViewmoreEnable(true);
        } else {
            setViewmoreEnable(false);
        }
    }, [myJobs.data]);

    useEffect(() => {
        MyJobsActions.updateSearch({ limit });
    }, [limit]);

    const handleChangeActionType = async (action) => {
        await MyJobsActions.updateSearch({ actions: action });
        await MyJobsActions.getMyJobs();
    };

    const onChangeLimit = (limit) => {
        setLimit(limit);
    };

    const onSubmit = async () => {
        await MyJobsActions.updateSearch({ limit: myJobs.search.limit + limit });
        await MyJobsActions.getMyJobs();
    };

    const checkJobStatus = (jobId) => {
        return new Promise(async (resolve, reject) => {
            try {
                const response = await MyJobsActions.getJobStatus(jobId);
                const { results } = response.data;
                // console.log('results', results[0]);
                resolve(results[0].status);
            } catch (error) {
                reject("FAILED");
            }
        });
    };

    const startPollingJobStatus = async (jobId) => {
        const doSomething = (jobId) => {
            return new Promise(resolve =>
                setTimeout(async () => {
                    const results = await checkJobStatus(jobId);
                    resolve(results);
                }, 2000));
        };
        const loop = (jobId) =>
            doSomething(jobId).then(results => {
                if (results === "SUCCEED" || results === "FAILED") {
                    return results;
                } else {
                    return loop(jobId);
                }
            });
        return new Promise((resolve, reject) => {
            loop(jobId).then((results) => {
                // console.log('all done!', results)
                resolve(results);
            }).catch((error) => {
                reject(error);
            });
        });
    };

    const onRetry = async (jobId) => {
        try {
            await MyJobsActions.retryJob(jobId);
            await MyJobsActions.getMyJobs();
            const status = await startPollingJobStatus(jobId);
            const _response = await MyJobsActions.getJobStatus(jobId);
            const { results } = _response.data;
            if (results && results[0].status === "FAILED") {
                const action = results[0].name;
                toastJustMessage("error", `${action} error.`);
            } else {
                let partialComplete = "";
                const action = results[0].name;
                if (results && results[0].partialFailure) {
                    partialComplete = "Partial ";
                }
                toastJustMessage("success", `${partialComplete}${action} completed.`);
            }
            await MyJobsActions.getMyJobs();
        } catch (error) {
            toastErrorMessage(error);
            await MyJobsActions.getMyJobs();
        }
    };

    const toastJustMessage = (type, message) => {
        NotificationActions.setNotification({
            type,
            contents: `${message}`,
        });
    };

    const toastErrorMessage = (error) => {
        const errorStatus = error.response && error.response.status;
        if (errorStatus === 400) {
            const errorMessage = (error.response.data && error.response.data.message);
            NotificationActions.setNotification({
                type: "error",
                contents: `[Error:${errorStatus}] => ${
                    errorMessage
                }`,
            });
        }
    };

    const onClickToggleFileListExpander = async (item) => {
        if (throttleHandler) {
            return;
        }
        if (item.isFolded) {
            const job = {
                ...item,
                isFolded: !item.isFolded,
            };
            await MyJobsActions.updateJob({ job });
        } else {
            throttleHandler = true;
            try {
                const { data } = await MyJobsActions.getMyJobTask({ jobId: item.jobId });
                // console.log('get my tasks', data);
                const newTasks = data.results || [];
                const job = {
                    ...item,
                    isFolded: !item.isFolded,
                    tasks: newTasks,
                };
                await MyJobsActions.updateJob({ job });
                throttleHandler = false;
            } catch (error) {
                throttleHandler = false;
            }
        }
    };

    const renderFileStatus = (item) => {
        // console.log('check items:', item)
        let className, text;
        const label = actionTypes.filter(a => a.value === item.action) && actionTypes.filter(a => a.value === item.action)[0].label;
        switch (item.status) {
            case "SUBMITTED":
                className = "text-primary";
                text = t(`common::label::${"Pending {{type}}..."}`, { type: label });
                break;
            case "SUCCEED":
                className = item.partialFailure ? "text-danger" : "text-success";
                text = item.partialFailure ? t(`common::label::${"Partial {{type}} to completed."}`, { type: label }) : t(`common::label::${"{{type}} completed."}`, { type: label });
                break;
            case "FAILED":
                className = "text-danger";
                text = t(`common::label::${"{{type}} error."}`, { type: label });
                break;
            case "CANCEL":
            case "CANCELED":
                className = "text-cancel";
                text = t(`common::label::${"{{type}} canceled."}`, { type: label });
                break;
            default:
                className = "text-primary";
                text = item.action === "MOVE" ?
                    `${t(`common::label::${"Moving"}`)}...` :
                    item.action === "COPY" ?
                        `${t(`common::label::${"Copying to"}`)}...` :
                        item.action === "DELETE" ?
                            `${t(`common::label::${"Deleting"}`)}...` :
                            item.action === "RENAME" ?
                                `${t(`common::label::${"Renaming"}`)}...` : "";
                break;
        }
        return <React.Fragment>
            <small className={`${className}`}>
                {`${text}`}
            </small>
        </React.Fragment>;
    };

    const myJobsData = myJobs.data;
    // console.log('myJobsData', myJobsData)
    return (
        <React.Fragment>
            <div className="panel-body">
                <div className="box-scroll">
                    <WithSelect
                        defaultValue={myJobs.search.actions}
                        options={actionTypes}
                        onChange={(option) => {
                            handleChangeActionType(option.value);
                        }}
                    />
                    {myJobsData.length > 0 ?
                        <div className="list-process">
                            <ul>
                                {
                                    myJobsData.map(item => {
                                        const actionTypesLabel = actionTypes.filter(a => a.value === item.action)[0].label;
                                        return <li key={item.jobId}>
                                            <div className="head">
                                                <div>
                                                <span>
                                                    <i
                                                        className={`sprite sprite-job-type-${item.action.toLowerCase() === "copy" ? "copyto" : item.action.toLowerCase()} text-nowrap`}
                                                        data-tooltip={t(`common::label::${actionTypesLabel}`)}
                                                    ></i>
                                                </span>
                                                    <hr />
                                                    <span className="status-flag">
                                                    <StatusBadge status={item.status} />
                                                </span>
                                                    {
                                                        (item.status === "FAILED" || item.partialFailure) &&
                                                        <span className="error"
                                                              data-tooltip={`${item.summary && item.summary.exitCode ? t(`common::code::${item.summary.exitCode}`) : ""}`}>
                                                        <i className="sprite sprite-error"></i>
                                                    </span>
                                                    }
                                                </div>
                                                <div>
                                                    {
                                                        (item.status === "FAILED" || item.partialFailure) && (
                                                            <button
                                                                type={"button"}
                                                                className={"btn btn-icon btn-sm"}
                                                                data-tooltip={t(`common::label::Retry`)}
                                                                onClick={(e) => {
                                                                    onRetry(item.jobId);
                                                                }}
                                                            >
                                                                <i className={"sprite sprite-retry"}></i>
                                                                <span
                                                                    className="hidden">{t(`common::label::Retry`, `Retry`)}</span>
                                                            </button>)
                                                    }
                                                </div>
                                            </div>
                                            <div className="date">
                                                <dl>
                                                    <dt>{`Job ID`}</dt>
                                                    <dd>
                                                        {`${item.jobId || "-"}`}
                                                    </dd>
                                                </dl>
                                                {
                                                    item.startAt ?
                                                        <dl>
                                                            <dt>{`Start date`}</dt>
                                                            <dd>
                                                                {`${moment(item.startAt).format("YYYY-MM-DD HH:mm:ss")}`}
                                                            </dd>
                                                        </dl> : ""
                                                }
                                                {
                                                    item.endAt ?
                                                        <dl>
                                                            <dt>{`End date`}</dt>
                                                            <dd>
                                                                {`${moment(item.endAt).format("YYYY-MM-DD HH:mm:ss")}`}
                                                            </dd>
                                                        </dl> : ""
                                                }
                                            </div>
                                            <div className="list">
                                                {
                                                    item.taskCount > 0 && <button
                                                        type="button"
                                                        className="btn btn-icon-solid btn-fold"
                                                        onClick={(e) => {
                                                            onClickToggleFileListExpander(item);
                                                        }}
                                                    >
                                                        <i className={
                                                            "sprite " +
                                                            (item.isFolded ? "sprite-angle-up" : "sprite-angle-down")
                                                        }
                                                        ></i>
                                                    </button>
                                                }

                                                <div className="file">
                                                    {
                                                        item.taskCount > 0 &&
                                                        <div className="file-info">
                                                        <span className={"file-size"}>
                                                            <Trans
                                                                i18nKey="common::msg::Detailed view of <0> {{number}} </0> tasks"
                                                                values={{ number: (item.taskCount) || 0 }}
                                                                components={[<strong
                                                                    className="text-primary"></strong>]} />
                                                            {/* { t(`common::label:: - Total {{totalSize}}`, {
                                                                totalSize: convertToByte(item.size),
                                                            })} */}
                                                        </span>
                                                        </div>
                                                    }

                                                    <div className="file-status">
                                                        {
                                                            renderFileStatus(item)
                                                        }
                                                    </div>
                                                </div>
                                                {
                                                    (item.isFolded && item.tasks) && item.tasks.map((file, index) => {
                                                        let displayName = file.displayName;
                                                        if (file.metadata.isFolder) {
                                                            const splitPath = file.displayName.slice(0, -1).split("/");
                                                            displayName = splitPath[splitPath.length - 1];
                                                        }
                                                        const fromFile = `${file.metadata && `${file.metadata.fromBucket}/${file.metadata.fromKey && file.metadata.fromKey}`}`;
                                                        const toFile = `${file.metadata && `${file.metadata.toBucket}/${file.metadata.toKey && file.metadata.toKey}`}`;
                                                        const fromBucketPath = `${fromFile.replace(`${file.metadata.isFolder ? `${displayName}/` : displayName}`, "")}`;
                                                        const toBucketPath = `${toFile.replace(`${file.metadata.isFolder ? `${displayName}/` : displayName}`, "")}`;

                                                        const actionedTarget = Object.keys(item.metadata).length > 0 && {
                                                            displayName: item.metadata.items && item.metadata.items[0].fromFileName || "",
                                                            isFolder: item.metadata.items && item.metadata.items[0].isFolder || "",
                                                            toKey: item.metadata.items && item.metadata.items[0].toKey || "",
                                                            fromKey: item.metadata.items && item.metadata.items[0].fromKey || "",
                                                            fromBucket: item.metadata && item.metadata.fromBucket || "",
                                                        };

                                                        if (actionedTarget) {
                                                            actionedTarget.toName = `${actionedTarget.toKey.lastIndexOf("/") === -1 ? actionedTarget.toKey : actionedTarget.toKey.slice(actionedTarget.toKey.lastIndexOf("/") + 1)}`;
                                                            actionedTarget.fromName = `${actionedTarget.fromKey.lastIndexOf("/") === -1 ? actionedTarget.fromKey : actionedTarget.fromKey.slice(actionedTarget.fromKey.lastIndexOf("/") + 1)}`;
                                                        }

                                                        return <div className="file" key={file.taskId}>
                                                            {
                                                                item.action !== "RENAME" ?
                                                                    <div className="file-info">
                                                                        <span className="file-type">
                                                                            <ObjectIcon path={displayName}
                                                                                        isFolder={file.metadata && file.metadata.isFolder}
                                                                                        isFolderDisabled={file.metadata && file.metadata.isFolder} />
                                                                        </span>
                                                                        {
                                                                            file.status === "FAILED" &&
                                                                            <span className={"file-error"}
                                                                                  data-tooltip={`${file.summary && file.summary.exitCode ? t(`common::code::${file.summary.exitCode}`) : ""}`}>
                                                                                <i className="sprite sprite-error"></i>
                                                                            </span>
                                                                        }
                                                                        <span className="file-name">
                                                                            <strong>
                                                                                {displayName}
                                                                            </strong>
                                                                        </span>
                                                                        <span className={"file-size"}>
                                                                            {file.metadata && file.metadata.isFolder ? "" : convertToByte((file.metadata && file.metadata.fromFileSize) || 0)}
                                                                        </span>
                                                                    </div> :
                                                                    <React.Fragment>
                                                                        {
                                                                            index === 0 && actionedTarget &&
                                                                            <React.Fragment>
                                                                                <div className="file-info">
                                                                                    <span className="file-type">
                                                                                        {/* <i className="sprite sprite-folder-open"></i> */}
                                                                                        {
                                                                                            actionedTarget.isFolder ?
                                                                                                <i className="sprite sprite-folder-open"></i> :
                                                                                                <ObjectIcon
                                                                                                    path={actionedTarget.displayName}
                                                                                                    isFolder={actionedTarget.isFolder}
                                                                                                    isFolderDisabled={actionedTarget.isFolder} />
                                                                                        }
                                                                                    </span>
                                                                                    <span className="file-name">
                                                                                        <small
                                                                                            className="text-secondary text-smaller">
                                                                                            {actionedTarget.fromName}
                                                                                        </small>
                                                                                    </span>
                                                                                </div>
                                                                                <div className="file-path">
                                                                                    <div>
                                                                                        <span><i
                                                                                            className="sprite sprite-path"></i></span>
                                                                                        <p>
                                                                                            {
                                                                                                `${actionedTarget.fromBucket}${actionedTarget.fromKey.replace(actionedTarget.fromName, "")}`
                                                                                            }
                                                                                        </p>
                                                                                    </div>
                                                                                </div>
                                                                                <div className="file-result">
                                                                                    <span
                                                                                        className="file-type file-to-be"><i
                                                                                        className="sprite sprite-caret-right-gray"></i><i
                                                                                        className="sprite sprite-caret-right-gray"></i></span>
                                                                                    <span className="file-name">
                                                                                      <strong>  {actionedTarget.toName}</strong>
                                                                                    </span>
                                                                                </div>
                                                                            </React.Fragment>
                                                                        }
                                                                    </React.Fragment>
                                                            }
                                                            {
                                                                item.action === "RENAME" ?
                                                                    <React.Fragment>
                                                                        {
                                                                            displayName !== (actionedTarget && actionedTarget.fromName || "") &&
                                                                            <React.Fragment>
                                                                                <div className="file-info indent">
                                                                                    <span className="file-type">
                                                                                        <ObjectIcon path={displayName}
                                                                                                    isFolder={file.metadata && file.metadata.isFolder}
                                                                                                    isFolderDisabled={file.metadata && file.metadata.isFolder} />
                                                                                    </span>
                                                                                    {
                                                                                        file.status === "FAILED" &&
                                                                                        <span className={"file-error"}
                                                                                              data-tooltip={`${file.summary && file.summary.exitCode ? t(`common::code::${file.summary.exitCode}`) : ""}`}>
                                                                                            <i className="sprite sprite-error"></i>
                                                                                        </span>
                                                                                    }
                                                                                    <span className="file-name">
                                                                                            {displayName} - {file.metadata && file.metadata.isFolder ? "" : convertToByte((file.metadata && file.metadata.fromFileSize) || 0)}
                                                                                    </span>
                                                                                </div>
                                                                                {/*<div className="file-path">*/}
                                                                                {/*    <div>*/}
                                                                                {/*        <span><i className="sprite sprite-path"></i></span>*/}
                                                                                {/*        <p>*/}
                                                                                {/*        {*/}
                                                                                {/*            (file.metadata && file.metadata.toKey || '')*/}
                                                                                {/*        }*/}
                                                                                {/*        </p>*/}
                                                                                {/*    </div>*/}
                                                                                {/*</div>*/}
                                                                            </React.Fragment>
                                                                        }

                                                                    </React.Fragment> :
                                                                    item.action === "MOVE" || item.action === "COPY" ?
                                                                        <div className="file-path">
                                                                            <div>
                                                                                <span><i
                                                                                    className="sprite sprite-output-path"></i></span>
                                                                                <p>
                                                                                    {
                                                                                        fromBucketPath
                                                                                    }
                                                                                </p>
                                                                            </div>
                                                                            <div>
                                                                                <span><i
                                                                                    className="sprite sprite-input-path"></i></span>
                                                                                <p>
                                                                                    {
                                                                                        toBucketPath
                                                                                    }
                                                                                </p>
                                                                            </div>
                                                                        </div> :
                                                                        <div className="file-path">
                                                                            <div>
                                                                                <span><i className="sprite sprite-path"></i></span>
                                                                                <p>
                                                                                    {
                                                                                        fromBucketPath
                                                                                    }
                                                                                </p>
                                                                            </div>
                                                                        </div>
                                                            }
                                                        </div>;
                                                    })
                                                }
                                            </div>
                                        </li>;
                                    })
                                }

                            </ul>
                        </div>
                        :
                        <div className={"empty"}>
                            <i className={"sprite sprite-nojob"}></i>
                            <p>
                                {t(`common::msg::${"No {{name}} searched."}`, {
                                    name: t(`common::label::${"Jobs"}`),
                                })}
                            </p>
                        </div>
                    }
                </div>
                {myJobsData.length > 0 &&
                    <div className="panel-footer">
                        <WithSelect
                            defaultValue={limit}
                            options={CONSTANTS("LIST_LIMITS")}
                            onChange={(option) => onChangeLimit(option.value)}
                        />
                        {viewmoreEnable ? (
                            <button
                                type={"button"}
                                className={"btn btn-outline-default"}
                                onClick={(e) => {
                                    e.preventDefault();
                                    onSubmit();
                                }}
                            >
                                <span>{`View More`}</span>
                                <i className="sprite sprite-angle-down"></i>
                            </button>
                        ) : (
                            ""
                        )}
                    </div>
                }
            </div>
        </React.Fragment>
    );
};

export default withTranslation()(FileJobPanel);
